import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Trash2, Maximize2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUpdateFeaturedLayout, useRemoveFeaturedItem, useAddFeaturedItem } from '@/hooks/use-featured';
import type { FeaturedItem } from '@/types/moodboards';

interface FeaturedEditorProps {
  items: FeaturedItem[];
  onClose: () => void;
  userId: string;
}

export function FeaturedEditor({ items, onClose, userId }: FeaturedEditorProps) {
  const [localItems, setLocalItems] = useState(items);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const updateLayout = useUpdateFeaturedLayout();
  const removeItem = useRemoveFeaturedItem();
  const addItem = useAddFeaturedItem();

  const GRID_COLS = 12;
  const GAP = 16;
  const ITEM_HEIGHT = 200;

  const handleSave = async () => {
    await updateLayout.mutateAsync({
      items: localItems.map(item => ({
        id: item.id,
        grid_x: item.grid_x,
        grid_y: item.grid_y,
        grid_width: item.grid_width,
        grid_height: item.grid_height,
      })),
    });
    onClose();
  };

  const handleRemove = async (itemId: string) => {
    await removeItem.mutateAsync(itemId);
    setLocalItems(localItems.filter(item => item.id !== itemId));
  };

  const handleAddItem = async (itemType: 'media' | 'loop' | 'album' | 'moodboard') => {
    try {
      const newItem = await addItem.mutateAsync({
        item_type: itemType,
        grid_width: 1,
        grid_height: 1,
      });
      setLocalItems([...localItems, newItem]);
      setShowAddDialog(false);
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const handleResize = (itemId: string, direction: 'width' | 'height', delta: number) => {
    setLocalItems(prev =>
      prev.map(item => {
        if (item.id !== itemId) return item;
        
        if (direction === 'width') {
          const newWidth = Math.max(1, Math.min(GRID_COLS - item.grid_x, item.grid_width + delta));
          return { ...item, grid_width: newWidth };
        } else {
          const newHeight = Math.max(1, item.grid_height + delta);
          return { ...item, grid_height: newHeight };
        }
      })
    );
  };

  const getItemStyle = (item: FeaturedItem) => {
    const colWidth = `calc((100% - ${GAP * (GRID_COLS - 1)}px) / ${GRID_COLS})`;
    
    return {
      left: `calc(${item.grid_x} * (${colWidth} + ${GAP}px))`,
      top: `${item.grid_y * (ITEM_HEIGHT + GAP)}px`,
      width: `calc(${item.grid_width} * ${colWidth} + ${(item.grid_width - 1) * GAP}px)`,
      height: `${item.grid_height * ITEM_HEIGHT + (item.grid_height - 1) * GAP}px`,
    };
  };

  const containerHeight = localItems.length > 0
    ? Math.max(...localItems.map(item => (item.grid_y + item.grid_height) * (ITEM_HEIGHT + GAP)))
    : ITEM_HEIGHT;

  return (
    <div className="relative">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted rounded-lg">
        <div>
          <h3 className="font-semibold">Edit Mode</h3>
          <p className="text-sm text-muted-foreground">
            Click items to select, use buttons to resize
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAddDialog(!showAddDialog)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateLayout.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {updateLayout.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Add Item Dialog */}
      {showAddDialog && (
        <div className="mb-4 p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-3">Select item type to add:</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Button variant="outline" onClick={() => handleAddItem('media')} className="h-20 flex-col gap-2">
              <span className="text-2xl">🖼️</span>
              <span className="text-xs">Media</span>
            </Button>
            <Button variant="outline" onClick={() => handleAddItem('loop')} className="h-20 flex-col gap-2">
              <span className="text-2xl">🔁</span>
              <span className="text-xs">Loop</span>
            </Button>
            <Button variant="outline" onClick={() => handleAddItem('album')} className="h-20 flex-col gap-2">
              <span className="text-2xl">📁</span>
              <span className="text-xs">Album</span>
            </Button>
            <Button variant="outline" onClick={() => handleAddItem('moodboard')} className="h-20 flex-col gap-2">
              <span className="text-2xl">🎨</span>
              <span className="text-xs">Moodboard</span>
            </Button>
          </div>
        </div>
      )}

      {/* Grid Container */}
      <div className="relative w-full border-2 border-dashed border-muted rounded-lg p-4" style={{ height: `${containerHeight}px` }}>
        {localItems.map((item) => {
          const isSelected = selectedId === item.id;

          return (
            <motion.div
              key={item.id}
              className={`absolute rounded-xl overflow-hidden transition-all ${
                isSelected ? 'ring-2 ring-primary z-10' : 'ring-1 ring-border'
              }`}
              style={getItemStyle(item)}
              onClick={() => setSelectedId(item.id)}
            >
              {/* Preview */}
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <span className="text-4xl">
                  {item.item_type === 'moodboard' && '🎨'}
                  {item.item_type === 'album' && '📁'}
                  {item.item_type === 'loop' && '🔁'}
                  {item.item_type === 'media' && '🖼️'}
                </span>
              </div>

              {/* Controls */}
              {isSelected && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResize(item.id, 'width', -1);
                    }}
                  >
                    W-
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResize(item.id, 'width', 1);
                    }}
                  >
                    W+
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResize(item.id, 'height', -1);
                    }}
                  >
                    H-
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResize(item.id, 'height', 1);
                    }}
                  >
                    H+
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(item.id);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Size Label */}
              <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                {item.grid_width}x{item.grid_height}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
